import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { connect } from 'react-redux';
import { Pill, PillWithIcon } from '@els/els-react--pill';
import { TogglePanel, TogglePanelCollection } from '@els/els-react--toggle-panel';
import UserDatabases from '@Services/UserDatabases';
import Auth from '@Services/Auth';
import Breadcrumb from '@Services/Breadcrumb';
import ProfileSettings from '@Services/ProfileSettings/ProfileSettings';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import CommercialSubstancesPreferencesEditor from '@Components/CommercialSubstancesPreferences/CommercialSubstancesPreferencesEditor';
import { goToResultPage } from '@Containers/Breadcrumbs/BreadcrumbsHelper';
import {
  INITIAL_SELECTIONS,
  PREFERENCE_CONFIGS,
  SUPPLIERS_TAB_ID,
  TAB_CONFIGS,
  getPreferenceOptionsByTab,
  getPreferencePayload,
  getPreferenceSelections,
  getPersistablePreferenceSelections,
  getTabConfig,
  hasSelectedPreferences,
  normalizePreferenceValue,
  normalizeText,
} from '@Components/CommercialSubstancesPreferences/CommercialSubstancesPreferencesConstants';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Modal } from '@els/els-react--modal';
import {
  paramsForFiltersRequest as paramsForFiltersRequestSelector,
  paramsForFilterGroups as paramsForFilterGroupsSelector,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';
import { getNormalizedFilterGroupsAsync } from '@Containers/ResultsFilters/ResultsFiltersService';
import { activeBreadcrumbForDb as activeBreadcrumbForDbSelector } from '@Containers/Breadcrumbs/BreadcrumbsSelectors';
import { open as openLoginPromptDialog } from '@Containers/ResultsFilters/ResultsFiltersComponents/BarChartFilter/LoginPromptDialog/LoginPromptDialogActions';
import { showGlobalSpinner, hideGlobalSpinner } from '@Containers/GlobalSpinner/GlobalSpinnerActions';
import { limitToWithPreferenceProperties } from '@Containers/ResultsFilters/ResultsFiltersComponents/ResultsFiltersButtons/ResultsFiltersButtonsActions';
import { embedFacetsObj } from '@Containers/ResultsFilters/performSearchFilterAsync/facetsForTrackingUtils';
import { trackRedirectToPreferences } from '@Containers/ResultsFilters/ResultsFiltersComponents/BarChartFilter/HOCs/analitycs';

import './CommercialSubstancesPreferences.scss';

const SET_PREFERENCES_DESCRIPTION_ID = 'commercial-substances-preferences-set-description';
const SUMMARY_CONTENT_ID = 'commercial-substances-preferences-summary-content';
const SUMMARY_PANEL_ID = 'commercial-substances-preferences-summary-panel';
const MODAL_TITLE_ID = 'commercial-substances-preferences-modal-title';
const MODAL_TAB_PANEL_ID = 'commercial-substances-preferences-tab-panel';
const NO_RESULTS_MODAL_TITLE_ID = 'commercial-substances-preferences-no-results-modal-title';

function cloneSelections(selections) {
  return TAB_CONFIGS.reduce(
    (accumulator, tab) => ({
      ...accumulator,
      [tab.id]: [...(selections[tab.id] || [])],
    }),
    {}
  );
}

function areTabSelectionsEqual(tabId, firstSelections, secondSelections) {
  const firstValues = (firstSelections || INITIAL_SELECTIONS)[tabId] || [];
  const secondValues = (secondSelections || INITIAL_SELECTIONS)[tabId] || [];

  return (
    firstValues.length === secondValues.length &&
    firstValues.every((value, index) => normalizePreferenceValue(value) === normalizePreferenceValue(secondValues[index]))
  );
}

function areSelectionsEqual(firstSelections = INITIAL_SELECTIONS, secondSelections = INITIAL_SELECTIONS) {
  return TAB_CONFIGS.every((tab) => areTabSelectionsEqual(tab.id, firstSelections, secondSelections));
}

function getChangedTabIds(selections = INITIAL_SELECTIONS, previousSelections = INITIAL_SELECTIONS) {
  return TAB_CONFIGS.filter((tab) => !areTabSelectionsEqual(tab.id, selections, previousSelections)).map((tab) => tab.id);
}

function getBreadcrumbFilterPropertyId(filter) {
  const property = filter?.property || filter?.get?.('property');

  if (!property) {
    return property;
  }

  return property?.property || property?.get?.('property') || property;
}

function isPreferenceFilterIdForTab(filterId, tabId) {
  const preferenceConfig = PREFERENCE_CONFIGS[tabId];

  return normalizeText(preferenceConfig.filterId) === normalizeText(filterId);
}

function isExplicitlyAppliedPreferenceFilter(filter, tabId) {
  const preferenceConfig = PREFERENCE_CONFIGS[tabId];
  const facets = filter?._facetsForTracking || filter?.get?.('_facetsForTracking');
  const isPreference = facets?.isPreference || facets?.get?.('isPreference');

  return Boolean(isPreference) && normalizeText(preferenceConfig.resultFilterId) === normalizeText(getBreadcrumbFilterPropertyId(filter));
}

function hasPreferenceTabFilterInActiveBreadcrumb(activeBreadcrumb, tabId) {
  const breadcrumbFilters = activeBreadcrumb?.get?.('filters') || activeBreadcrumb?.filters;

  if (!breadcrumbFilters) {
    return false;
  }

  return breadcrumbFilters.some(
    (filter) =>
      isPreferenceFilterIdForTab(getBreadcrumbFilterPropertyId(filter), tabId) || isExplicitlyAppliedPreferenceFilter(filter, tabId)
  );
}

function hasPreferenceFilterInActiveBreadcrumb(activeBreadcrumb) {
  return TAB_CONFIGS.some((tab) => hasPreferenceTabFilterInActiveBreadcrumb(activeBreadcrumb, tab.id));
}

function toPlainBreadcrumb(breadcrumb) {
  return breadcrumb?.toJS ? breadcrumb.toJS() : breadcrumb;
}

function getRootBreadcrumb(activeBreadcrumb, databaseIdFallback) {
  const plainActiveBreadcrumb = toPlainBreadcrumb(activeBreadcrumb) || Breadcrumb.getActiveBreadcrumbForDb(databaseIdFallback);

  return Breadcrumb.retrieveRootActiveBreadcrumb(plainActiveBreadcrumb);
}

function getRootSearchFilterBaseParams(activeBreadcrumb, databaseIdFallback) {
  const rootBreadcrumb = getRootBreadcrumb(activeBreadcrumb, databaseIdFallback);

  if (!rootBreadcrumb) {
    return null;
  }

  return {
    searchId: rootBreadcrumb.searchId,
    context: rootBreadcrumb.context,
    databaseId: rootBreadcrumb.databaseId,
    resultName: rootBreadcrumb.resultName,
    hitSetKey: rootBreadcrumb.hitSetKey,
  };
}

function getSelectedValuesWithOptionAliases(selectedValues, tabId, optionsByTab = {}) {
  return selectedValues.flatMap((value) => {
    const selectedOption = optionsByTab[tabId]?.find((option) =>
      [option.id, option.name, option.label]
        .filter(Boolean)
        .some((optionValue) => normalizePreferenceValue(optionValue) === normalizePreferenceValue(value))
    );

    return [value, selectedOption?.id, selectedOption?.name, selectedOption?.label, ...(selectedOption?.aliases || [])].filter(Boolean);
  });
}

function resolveRootFacetIndexes(values, groups, tabId, optionsByTab) {
  return (values || [])
    .map((value) => {
      const aliases = getSelectedValuesWithOptionAliases([value], tabId, optionsByTab).map(normalizePreferenceValue);
      const group = (groups || []).find((item) => aliases.includes(normalizePreferenceValue(item.title)));
      return group ? group.index : null;
    })
    .filter((index) => index !== null && index !== undefined);
}

function buildPreferencePropertiesFromRootGroups(groupsByTab, selections, optionsByTab) {
  return TAB_CONFIGS.reduce((properties, tab) => {
    const values = selections[tab.id] || [];
    const groups = groupsByTab[tab.id] || [];

    if (values.length === 0 || groups.length === 0) {
      return properties;
    }

    const indexes = resolveRootFacetIndexes(values, groups, tab.id, optionsByTab);

    if (indexes.length === 0) {
      return properties;
    }

    properties.push({
      title: getTabConfig(tab.id).label,
      property: PREFERENCE_CONFIGS[tab.id].resultFilterId,
      indexes,
      ...embedFacetsObj({ name: getTabConfig(tab.id).label, values, isPreference: true }),
    });

    return properties;
  }, []);
}

export function hasSelectedPreferenceTabWithoutRootMatches(groupsByTab, selections, optionsByTab) {
  return TAB_CONFIGS.some((tab) => {
    const values = selections[tab.id] || [];

    if (values.length === 0) {
      return false;
    }

    const groups = groupsByTab[tab.id] || [];
    const indexes = resolveRootFacetIndexes(values, groups, tab.id, optionsByTab);

    return indexes.length === 0;
  });
}

function deriveAppliedSelectionsFromBreadcrumb(savedSelections = INITIAL_SELECTIONS, activeBreadcrumb = null) {
  return TAB_CONFIGS.reduce((accumulator, tab) => {
    accumulator[tab.id] = hasPreferenceTabFilterInActiveBreadcrumb(activeBreadcrumb, tab.id) ? [...(savedSelections[tab.id] || [])] : [];
    return accumulator;
  }, {});
}

function PreferenceApplyPill({ value, onAdd, disabled = false }) {
  return (
    <PillWithIcon
      className={disabled ? 'commercial-substances-preferences__pill--disabled' : ''}
      pillColor="n1"
      textColor="secondary"
      iconProps={{
        sprite: 'Plus',
        children: 'Apply',
      }}
      onClick={disabled ? undefined : onAdd}
      iconRight>
      {value}
    </PillWithIcon>
  );
}

PreferenceApplyPill.propTypes = {
  value: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function PreferenceRemovePill({ value, onRemove, disabled = false }) {
  return (
    <Pill disabled={disabled} onClose={disabled ? undefined : onRemove}>
      {value}
    </Pill>
  );
}

PreferenceRemovePill.propTypes = {
  value: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function ApplyPreferencesControl({ onApply, onEdit, disabled = false }) {
  return (
    <div className="commercial-substances-preferences__apply-control">
      <Button type="secondary" expanded disabled={disabled} onClick={onApply} data-e2e="commercial-substances-preferences-apply">
        Apply Preferences
      </Button>
      <ButtonWithIcon
        type="link"
        sprite="ChevronRight"
        iconRight
        linkIconSize="xs"
        className="commercial-substances-preferences__apply-edit"
        data-e2e="commercial-substances-preferences-edit"
        aria-haspopup="dialog"
        aria-controls="commercial-substances-preferences-modal"
        disabled={disabled}
        onClick={onEdit}>
        Edit preferences
      </ButtonWithIcon>
    </div>
  );
}

ApplyPreferencesControl.propTypes = {
  onApply: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function PreferenceNoResultsModal({ onClose, onEditPreferences, onSearchWithoutPreferences }) {
  return (
    <Modal
      id="commercial-substances-preferences-no-results-modal"
      label="No results match your preferences"
      className="commercial-substances-preferences-no-results-modal"
      aria-labelledby={NO_RESULTS_MODAL_TITLE_ID}
      onClose={onClose}
      shouldPreventClickOverlayToClose>
      <div className="commercial-substances-preferences-no-results-modal__content">
        <h2 id={NO_RESULTS_MODAL_TITLE_ID} className="commercial-substances-preferences-no-results-modal__title">
          No results match your preferences
        </h2>
        <p className="commercial-substances-preferences-no-results-modal__message">
          Your selected preferences returned no commercial substance results.
          <br />
          Adjust your preferences or search without them.
        </p>
        <div className="commercial-substances-preferences-no-results-modal__actions">
          <Button
            type="secondary"
            data-e2e="commercial-substances-preferences-search-without-preferences"
            onClick={onSearchWithoutPreferences}>
            Search without preferences
          </Button>
          <Button type="primary" data-e2e="commercial-substances-preferences-edit-no-results" onClick={onEditPreferences}>
            Edit Preferences
          </Button>
        </div>
      </div>
    </Modal>
  );
}

PreferenceNoResultsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onEditPreferences: PropTypes.func.isRequired,
  onSearchWithoutPreferences: PropTypes.func.isRequired,
};

function PreferencesSummary({
  isExpanded,
  onToggle,
  onEdit,
  onClearAll,
  disabled = false,
  savedSelections,
  appliedSelections,
  onApplySection,
  onUnapplySection,
}) {
  const preferencePills = TAB_CONFIGS.map((tab) => {
    const savedCount = (savedSelections[tab.id] || []).length;

    if (savedCount === 0) {
      return null;
    }

    const isApplied = (appliedSelections[tab.id] || []).length > 0;
    const label = `${getTabConfig(tab.id).label} (${savedCount})`;

    return isApplied ? (
      <PreferenceRemovePill key={tab.id} value={label} onRemove={() => onUnapplySection(tab.id)} disabled={disabled} />
    ) : (
      <PreferenceApplyPill key={tab.id} value={label} onAdd={() => onApplySection(tab.id)} disabled={disabled} />
    );
  }).filter(Boolean);

  return (
    <TogglePanelCollection
      data-e2e="commercial-substances-preferences-toggle-panels-collection"
      className="list-of-filters-collection commercial-substances-preferences__summary"
      headerLevel="3"
      thinKeyline
      noTopKeyline
      canCloseAll
      initialOpenPanels={isExpanded ? [SUMMARY_PANEL_ID] : []}>
      <TogglePanel
        id={SUMMARY_PANEL_ID}
        data-e2e="commercial-substances-preferences-summary-toggle"
        className="filter-panel commercial-substances-preferences__summary-panel"
        header="Preferences"
        onClick={onToggle}>
        <div id={SUMMARY_CONTENT_ID} className="commercial-substances-preferences__summary-content">
          <div className="commercial-substances-preferences__summary-pills">{preferencePills}</div>

          <div className="commercial-substances-preferences__summary-actions">
            <ButtonWithIcon
              type="link"
              sprite="ChevronRight"
              iconRight
              linkIconSize="xs"
              data-e2e="commercial-substances-preferences-edit"
              aria-haspopup="dialog"
              aria-controls="commercial-substances-preferences-modal"
              disabled={disabled}
              onClick={onEdit}>
              Edit preferences
            </ButtonWithIcon>
            <Button type="link" disabled={disabled} data-e2e="commercial-substances-preferences-clear-all" onClick={onClearAll}>
              Clear all
            </Button>
          </div>
        </div>
      </TogglePanel>
    </TogglePanelCollection>
  );
}

PreferencesSummary.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onApplySection: PropTypes.func.isRequired,
  onUnapplySection: PropTypes.func.isRequired,
  savedSelections: PropTypes.object.isRequired,
  appliedSelections: PropTypes.object.isRequired,
};

export function CommercialSubstancesPreferences({
  paramsForFiltersRequest,
  paramsForFilterGroups,
  limitToWithPreferenceProperties: limitToWithPreferencePropertiesAction,
  openLoginPromptDialog: openLoginPromptDialogAction,
  showGlobalSpinner: showGlobalSpinnerAction,
  hideGlobalSpinner: hideGlobalSpinnerAction,
  activeBreadcrumb,
}) {
  const databaseId = paramsForFiltersRequest?.databaseId;
  const isRcsDatabase = UserDatabases.isRCSDatabase(databaseId);

  const preferencesTriggerRef = useRef(null);
  const hasDraftEditsRef = useRef(false);
  const ownsApplySpinnerRef = useRef(false);
  const isApplyingPreferencesRef = useRef(false);

  const showApplySpinner = useCallback(() => {
    if (ownsApplySpinnerRef.current) {
      return;
    }

    ownsApplySpinnerRef.current = true;
    showGlobalSpinnerAction();
  }, [showGlobalSpinnerAction]);

  const hideApplySpinner = useCallback(() => {
    if (!ownsApplySpinnerRef.current) {
      return;
    }

    ownsApplySpinnerRef.current = false;
    hideGlobalSpinnerAction();
  }, [hideGlobalSpinnerAction]);

  const releaseApplySpinner = useCallback(() => {
    ownsApplySpinnerRef.current = false;
  }, []);

  useEffect(
    () => () => {
      if (ownsApplySpinnerRef.current) {
        ownsApplySpinnerRef.current = false;
        hideGlobalSpinnerAction();
      }
    },
    [hideGlobalSpinnerAction]
  );

  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNoResultsModalOpen, setIsNoResultsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(SUPPLIERS_TAB_ID);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isModalLoadingPreferences, setIsModalLoadingPreferences] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [isApplyingPreferences, setIsApplyingPreferences] = useState(false);
  const [hasLoadedProfilePreferences, setHasLoadedProfilePreferences] = useState(false);
  const [optionsByTab, setOptionsByTab] = useState(INITIAL_SELECTIONS);
  const [savedSelections, setSavedSelections] = useState(INITIAL_SELECTIONS);
  const [draftSelections, setDraftSelections] = useState(INITIAL_SELECTIONS);
  const [searchValues, setSearchValues] = useState({
    suppliers: '',
    currency: '',
    shipmentCountry: '',
    shippingTime: '',
  });
  const appliedSelections = useMemo(
    () => deriveAppliedSelectionsFromBreadcrumb(savedSelections, activeBreadcrumb),
    [savedSelections, activeBreadcrumb]
  );

  const loadProfilePreferences = useCallback(async () => {
    if (hasLoadedProfilePreferences) {
      return {
        currentOptionsByTab: optionsByTab,
        currentSelections: savedSelections,
      };
    }

    await ProfileSettings.loadProfileSettingsInfo();
    const combinedInfo = ProfileSettings.getCombinedInfo() || {};
    const user = ProfileSettings.get() || {};

    const currentOptionsByTab = getPreferenceOptionsByTab(combinedInfo);
    const currentSelections = getPreferenceSelections(user, currentOptionsByTab);

    setOptionsByTab(currentOptionsByTab);
    setSavedSelections(currentSelections);
    setHasLoadedProfilePreferences(true);
    return {
      currentOptionsByTab,
      currentSelections,
    };
  }, [hasLoadedProfilePreferences, optionsByTab, savedSelections]);

  useEffect(() => {
    if (!hasLoadedProfilePreferences || Auth.isAnonymousUser()) {
      return;
    }

    const user = ProfileSettings.get() || {};
    const nextSavedSelections = getPreferenceSelections(user, optionsByTab);

    setSavedSelections((currentSavedSelections) => {
      if (areSelectionsEqual(currentSavedSelections, nextSavedSelections)) {
        return currentSavedSelections;
      }

      return cloneSelections(nextSavedSelections);
    });
  }, [hasLoadedProfilePreferences, optionsByTab]);

  useEffect(() => {
    if (!isModalOpen || isModalLoadingPreferences || hasDraftEditsRef.current) {
      return;
    }

    setDraftSelections((currentDraftSelections) => {
      if (areSelectionsEqual(currentDraftSelections, savedSelections)) {
        return currentDraftSelections;
      }

      return cloneSelections(savedSelections);
    });
  }, [isModalLoadingPreferences, isModalOpen, savedSelections]);

  const applyPreferenceSelections = useCallback(
    async (selections = INITIAL_SELECTIONS) => {
      const rootBaseParams = getRootSearchFilterBaseParams(activeBreadcrumb, databaseId);

      if (!rootBaseParams) {
        return;
      }

      const tabsWithValues = TAB_CONFIGS.filter((tab) => (selections[tab.id] || []).length > 0);

      if (tabsWithValues.length === 0) {
        const rootBreadcrumb = getRootBreadcrumb(activeBreadcrumb, databaseId);

        if (rootBreadcrumb) {
          goToResultPage(rootBreadcrumb);
        }

        return;
      }

      showApplySpinner();

      try {
        const rootGroupParams = { ...paramsForFilterGroups, ...rootBaseParams };

        const groupResponses = await Promise.all(
          tabsWithValues.map((tab) =>
            getNormalizedFilterGroupsAsync({
              ...rootGroupParams,
              properties: [{ property: PREFERENCE_CONFIGS[tab.id].resultFilterId, all: true }],
            })
          )
        );

        const groupsByTab = tabsWithValues.reduce((accumulator, tab, index) => {
          accumulator[tab.id] = groupResponses[index]?.groups || [];
          return accumulator;
        }, {});

        const preferenceProperties = buildPreferencePropertiesFromRootGroups(groupsByTab, selections, optionsByTab);

        if (preferenceProperties.length === 0 || hasSelectedPreferenceTabWithoutRootMatches(groupsByTab, selections, optionsByTab)) {
          hideApplySpinner();
          setIsNoResultsModalOpen(true);
          return;
        }

        releaseApplySpinner();
        const filterResult = await limitToWithPreferencePropertiesAction(preferenceProperties, rootBaseParams, {
          shouldOpenZeroResultDialog: false,
          activateBaseBreadcrumbBeforeCreate: Boolean(rootBaseParams.resultName && hasPreferenceFilterInActiveBreadcrumb(activeBreadcrumb)),
        });

        if (filterResult?.hasResults === false) {
          setIsNoResultsModalOpen(true);
        }
      } catch (error) {
        hideApplySpinner();
        Logger.error('Failed to apply commercial substances preferences', error);
      }
    },
    [
      activeBreadcrumb,
      databaseId,
      hideApplySpinner,
      limitToWithPreferencePropertiesAction,
      optionsByTab,
      paramsForFilterGroups,
      releaseApplySpinner,
      showApplySpinner,
    ]
  );

  useEffect(() => {
    if (!isRcsDatabase) {
      return undefined;
    }

    let isMounted = true;

    const bootstrap = async () => {
      try {
        if (Auth.isAnonymousUser()) {
          setHasLoadedProfilePreferences(true);
          return;
        }

        await loadProfilePreferences();
      } catch (error) {
        Logger.error('Commercial substances preferences bootstrap failed', error);
      } finally {
        if (isMounted) {
          setIsLoadingPreferences(false);
        }
      }
    };

    setIsLoadingPreferences(true);
    bootstrap();

    return () => {
      isMounted = false;
    };
  }, [isRcsDatabase, loadProfilePreferences]);

  const supplierOptions = useMemo(() => optionsByTab[SUPPLIERS_TAB_ID] || [], [optionsByTab]);

  const supplierOptionsByName = useMemo(
    () =>
      supplierOptions.reduce((accumulator, option) => {
        accumulator[option.name] = option;
        return accumulator;
      }, {}),
    [supplierOptions]
  );

  const hasSavedPreferences = useMemo(() => hasSelectedPreferences(savedSelections), [savedSelections]);
  const hasAppliedPreferences = useMemo(() => hasSelectedPreferences(appliedSelections), [appliedSelections]);

  const focusPreferencesTrigger = () => {
    setTimeout(() => {
      const previousTrigger = preferencesTriggerRef.current;

      if (previousTrigger && document.body.contains(previousTrigger)) {
        previousTrigger.focus();
        return;
      }

      document
        .querySelector('[data-e2e="commercial-substances-preferences-edit"], [data-e2e="open-commercial-substances-preferences"]')
        ?.focus();
    }, 0);
  };

  const openModal = async (event) => {
    preferencesTriggerRef.current = event?.currentTarget || null;

    if (Auth.isAnonymousUser()) {
      openLoginPromptDialogAction();
      return;
    }

    trackRedirectToPreferences();

    try {
      setIsModalOpen(true);
      setIsModalLoadingPreferences(true);
      hasDraftEditsRef.current = false;
      setActiveTab(SUPPLIERS_TAB_ID);

      const profilePreferenceData = !hasLoadedProfilePreferences ? await loadProfilePreferences() : null;
      const nextSelections = profilePreferenceData?.currentSelections || savedSelections;

      setDraftSelections(cloneSelections(nextSelections));
      setSearchValues({
        suppliers: '',
        currency: '',
        shipmentCountry: '',
        shippingTime: '',
      });
    } catch (error) {
      Logger.error('Failed to open commercial substances preferences modal', error);
    } finally {
      setIsModalLoadingPreferences(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsModalLoadingPreferences(false);
    hasDraftEditsRef.current = false;
    focusPreferencesTrigger();
  };

  const updateDraftSelection = (tabId, value, checked) => {
    hasDraftEditsRef.current = true;
    setDraftSelections((currentSelections) => {
      const existingSelections = currentSelections[tabId] || [];
      const nextSelections = checked ? [...existingSelections, value] : existingSelections.filter((item) => item !== value);

      return {
        ...currentSelections,
        [tabId]: nextSelections,
      };
    });
  };

  const clearDraftSelection = (tabId) => {
    hasDraftEditsRef.current = true;
    setDraftSelections((currentSelections) => ({
      ...currentSelections,
      [tabId]: [],
    }));
  };

  const saveProfilePreferences = async (nextSelections) => {
    const persistableSelections = getPersistablePreferenceSelections(optionsByTab, nextSelections);
    const persistableSavedSelections = getPersistablePreferenceSelections(optionsByTab, savedSelections);
    const changedTabIds = getChangedTabIds(persistableSelections, persistableSavedSelections);
    const payload = getPreferencePayload(
      optionsByTab,
      persistableSelections,
      persistableSavedSelections,
      ProfileSettings.get() || {},
      changedTabIds
    );

    if (payload.length > 0) {
      await ProfileSettings.updatePreferences(payload, 'PREFERRED_SUPPLIERS', {
        overwriteSuppliers: changedTabIds.includes(SUPPLIERS_TAB_ID),
      });
    }

    setSavedSelections(cloneSelections(persistableSelections));
    return persistableSelections;
  };

  const applySelections = async (nextSelections = savedSelections) => {
    if (isApplyingPreferencesRef.current) {
      return;
    }

    isApplyingPreferencesRef.current = true;
    setIsApplyingPreferences(true);

    try {
      await applyPreferenceSelections(nextSelections);
    } finally {
      isApplyingPreferencesRef.current = false;
      setIsApplyingPreferences(false);
    }
  };

  const saveAndApplyPreferences = async (nextSelections) => {
    setIsSavingPreferences(true);

    try {
      const persistableSelections = await saveProfilePreferences(nextSelections);

      if (isModalOpen) {
        closeModal();
      }

      await applySelections(persistableSelections);
    } catch (error) {
      Logger.error('Failed to apply commercial substances preferences', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const applySection = async (tabId) => {
    await applySelections({
      ...cloneSelections(appliedSelections),
      [tabId]: [...(savedSelections[tabId] || [])],
    });
  };

  const applyAllSavedPreferences = async () => {
    await applySelections(savedSelections);
  };

  const unapplySection = async (tabId) => {
    await applySelections({
      ...cloneSelections(appliedSelections),
      [tabId]: [],
    });
  };

  const clearAllSelections = async () => {
    await applySelections(INITIAL_SELECTIONS);
  };

  const searchWithoutPreferences = async () => {
    setIsNoResultsModalOpen(false);
    await clearAllSelections();
  };

  const editPreferencesFromNoResults = async () => {
    setIsNoResultsModalOpen(false);
    await openModal();
  };

  if (!isRcsDatabase) {
    return null;
  }

  const modalTitle = hasSavedPreferences ? 'Edit commercial substances preferences' : 'Commercial substances preferences';
  let preferencesControl = null;

  if (isLoadingPreferences && !hasLoadedProfilePreferences && !isModalOpen) {
    preferencesControl = (
      <div className="commercial-substances-preferences__loading" role="status" aria-live="polite">
        <div className="commercial-substances-preferences__loading-set-button c-els-loader-shimmer" aria-hidden="true" />
        <span className="sr-only">Loading preferences</span>
      </div>
    );
  } else if (hasLoadedProfilePreferences && !hasSavedPreferences) {
    preferencesControl = (
      <div className="commercial-substances-preferences__set-button">
        <span id={SET_PREFERENCES_DESCRIPTION_ID} className="sr-only">
          Configure preferred suppliers, currencies, shipping countries, and shipping times
        </span>
        <Button
          type="secondary"
          expanded
          onClick={openModal}
          data-e2e="open-commercial-substances-preferences"
          aria-describedby={SET_PREFERENCES_DESCRIPTION_ID}
          aria-haspopup="dialog"
          aria-controls="commercial-substances-preferences-modal">
          Set Preferences
        </Button>
      </div>
    );
  } else if (hasLoadedProfilePreferences && !hasAppliedPreferences) {
    preferencesControl = <ApplyPreferencesControl onApply={applyAllSavedPreferences} onEdit={openModal} disabled={isApplyingPreferences} />;
  } else if (hasLoadedProfilePreferences) {
    preferencesControl = (
      <PreferencesSummary
        isExpanded={isSummaryExpanded}
        savedSelections={savedSelections}
        appliedSelections={appliedSelections}
        disabled={isApplyingPreferences}
        onToggle={() => {
          setIsSummaryExpanded((currentValue) => !currentValue);
        }}
        onEdit={openModal}
        onClearAll={() => {
          clearAllSelections();
        }}
        onApplySection={(tabId) => {
          applySection(tabId);
        }}
        onUnapplySection={(tabId) => {
          unapplySection(tabId);
        }}
      />
    );
  }

  return (
    <section className="commercial-substances-preferences">
      {preferencesControl}

      {isModalOpen ? (
        <Modal
          id="commercial-substances-preferences-modal"
          label={modalTitle}
          className="commercial-substances-preferences-modal"
          aria-labelledby={MODAL_TITLE_ID}
          onClose={closeModal}
          shouldPreventClickOverlayToClose>
          <div className="u-els-padding-bottom commercial-substances-preferences-modal__header">
            <h2 id={MODAL_TITLE_ID} className="commercial-substances-preferences-modal__title">
              {modalTitle}
            </h2>
          </div>

          {isModalLoadingPreferences ? (
            <div className="commercial-substances-preferences-modal__loading">
              <RxSpinnerComponent autoStart spinnerId="commercial-substances-preferences-spinner" />
            </div>
          ) : (
            <CommercialSubstancesPreferencesEditor
              activeTab={activeTab}
              panelId={MODAL_TAB_PANEL_ID}
              selections={draftSelections}
              searchValues={searchValues}
              supplierOptions={supplierOptions}
              supplierOptionsByName={supplierOptionsByName}
              optionsByTab={optionsByTab}
              onTabChange={setActiveTab}
              onSearchChange={(tabId, value) => {
                setSearchValues((currentValue) => ({
                  ...currentValue,
                  [tabId]: value,
                }));
              }}
              onToggleSelection={updateDraftSelection}
              onClearSelection={clearDraftSelection}
              onCancel={closeModal}
              onSave={() => {
                saveAndApplyPreferences(draftSelections);
              }}
              isSaving={isSavingPreferences}
              saveLabel="Save and apply"
              cancelLabel="Cancel"
            />
          )}
        </Modal>
      ) : null}

      {isNoResultsModalOpen ? (
        <PreferenceNoResultsModal
          onClose={searchWithoutPreferences}
          onEditPreferences={editPreferencesFromNoResults}
          onSearchWithoutPreferences={searchWithoutPreferences}
        />
      ) : null}
    </section>
  );
}

CommercialSubstancesPreferences.propTypes = {
  paramsForFiltersRequest: PropTypes.object,
  paramsForFilterGroups: PropTypes.object,
  limitToWithPreferenceProperties: PropTypes.func.isRequired,
  openLoginPromptDialog: PropTypes.func.isRequired,
  showGlobalSpinner: PropTypes.func.isRequired,
  hideGlobalSpinner: PropTypes.func.isRequired,
  activeBreadcrumb: PropTypes.instanceOf(Map),
};

const mapStateToProps = (state) => ({
  paramsForFiltersRequest: paramsForFiltersRequestSelector(state),
  paramsForFilterGroups: paramsForFilterGroupsSelector(state),
  activeBreadcrumb: activeBreadcrumbForDbSelector(state),
});

const mapDispatchToProps = {
  limitToWithPreferenceProperties,
  openLoginPromptDialog,
  showGlobalSpinner,
  hideGlobalSpinner,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommercialSubstancesPreferences);
